import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Pencil, Trash2, Eye, Heart, DollarSign, 
  Check, X, Image, Loader2, AlertCircle 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { API } from '../config/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const categoryLabels = {
  photography: 'Photography',
  fine_art: 'Fine Art',
  film: 'Film',
};

export default function MyWorks() {
  const navigate = useNavigate();
  const { isAuthenticated, isArtist, isAdmin, loading: authLoading } = useAuth();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', price: '' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchMyArtworks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/my/artworks`);
      setArtworks(response.data.artworks);
    } catch {
      toast.error('Failed to load your artworks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || (!isArtist && !isAdmin)) {
      navigate('/');
      return;
    }
    fetchMyArtworks();
  }, [authLoading, isAuthenticated, isArtist, isAdmin, navigate, fetchMyArtworks]);

  const openEdit = (artwork) => {
    setEditingArtwork(artwork);
    setEditForm({
      title: artwork.title,
      description: artwork.description,
      price: artwork.price.toString(),
    });
  };

  const handleSave = async () => {
    if (!editForm.title.trim() || !editForm.description.trim() || !editForm.price) {
      toast.error('Please fill in all fields');
      return;
    }
    const price = parseFloat(editForm.price);
    if (isNaN(price) || price < 1) {
      toast.error('Price must be at least $1.00');
      return;
    }

    try {
      setSaving(true);
      const response = await axios.put(`${API}/artworks/${editingArtwork.id}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        price,
      });
      setArtworks(prev => prev.map(a => a.id === editingArtwork.id ? response.data : a));
      setEditingArtwork(null);
      toast.success('Artwork updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (artworkId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    try {
      setDeletingId(artworkId);
      await axios.delete(`${API}/artworks/${artworkId}`);
      setArtworks(prev => prev.filter(a => a.id !== artworkId));
      toast.success('Artwork deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-4">
          {[...Array(3)].map((_, i) => <div key={`skeleton-${i}`} className="h-28 skeleton" />)}
        </div>
      </div>
    );
  }

  if (!isAuthenticated || (!isArtist && !isAdmin)) return null;

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Manage</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#F5F5F0]">
              My Works
            </h1>
            <p className="mt-2 text-[#71717A]">
              {artworks.length} {artworks.length === 1 ? 'artwork' : 'artworks'}
            </p>
          </div>
          <Link to="/upload" className="btn-primary flex items-center gap-2" data-testid="upload-new-btn">
            <Image size={16} />
            Upload New
          </Link>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-28 skeleton" />
            ))}
          </div>
        ) : artworks.length === 0 ? (
          <div className="text-center py-24">
            <Image className="mx-auto text-[#71717A] mb-6" size={48} />
            <p className="text-[#71717A] text-lg">No artworks yet</p>
            <p className="text-[#71717A] mt-2">Upload your first piece to get started</p>
            <Link to="/upload" className="btn-primary inline-flex items-center gap-2 mt-6">
              Upload Artwork
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {artworks.map(artwork => (
              <div
                key={artwork.id}
                className="flex flex-col sm:flex-row gap-4 p-4 bg-[#121212] border border-white/10 hover:border-white/20 transition-colors"
                data-testid={`my-artwork-${artwork.id}`}
              >
                {/* Thumbnail */}
                <Link to={`/artwork/${artwork.id}`} className="flex-shrink-0">
                  <img
                    src={artwork.thumbnail_url || artwork.preview_url || artwork.image_url}
                    alt={artwork.title}
                    className="w-full sm:w-28 h-28 object-cover"
                  />
                </Link>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link 
                        to={`/artwork/${artwork.id}`}
                        className="font-serif text-lg text-[#F5F5F0] hover:text-[#D4AF37] transition-colors truncate block"
                      >
                        {artwork.title}
                      </Link>
                      <span className="text-xs text-[#71717A] uppercase tracking-wider">
                        {categoryLabels[artwork.category] || artwork.category}
                      </span>
                    </div>
                    <span className="price-tag text-lg flex-shrink-0">
                      ${artwork.price?.toFixed(2)}
                    </span>
                  </div>

                  <p className="text-[#A1A1AA] text-sm mt-2 line-clamp-2">
                    {artwork.description}
                  </p>

                  <div className="flex items-center justify-between mt-3">
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-[#71717A] text-sm">
                      <span className="flex items-center gap-1">
                        <Eye size={14} /> {artwork.view_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart size={14} /> {artwork.like_count || 0}
                      </span>
                      {artwork.is_sold && (
                        <span className="text-[#10B981] font-medium">Sold</span>
                      )}
                      {!artwork.is_approved && (
                        <span className="flex items-center gap-1 text-[#F59E0B]">
                          <AlertCircle size={14} /> Pending Review
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {!artwork.is_sold && (
                        <button
                          onClick={() => openEdit(artwork)}
                          className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
                          title="Edit"
                          data-testid={`edit-btn-${artwork.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(artwork.id, artwork.title)}
                        disabled={deletingId === artwork.id}
                        className="p-2 text-[#A1A1AA] hover:text-[#EF4444] hover:bg-white/5 transition-colors"
                        title="Delete"
                        data-testid={`delete-btn-${artwork.id}`}
                      >
                        {deletingId === artwork.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingArtwork} onOpenChange={(open) => !open && setEditingArtwork(null)}>
          <DialogContent className="bg-[#121212] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#F5F5F0]">
                Edit Artwork
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="form-label text-sm">Title</label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="form-input"
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <label className="form-label text-sm">Description</label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="form-input resize-none"
                  rows={4}
                  data-testid="edit-description-input"
                />
              </div>
              <div>
                <label className="form-label text-sm">Price (USD)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  value={editForm.price}
                  onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                  className="form-input"
                  data-testid="edit-price-input"
                />
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setEditingArtwork(null)}
                className="btn-secondary"
                data-testid="edit-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary"
                data-testid="edit-save-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
